/**
 * User Roles Panel
 *
 * Shown on the user detail page: which roles this user holds, assign a new
 * role (optionally time-boxed), revoke. Visible to holders of
 * userMgmt.role.view; mutations need userMgmt.role.assign.
 * APIs: /api/rbac/assignments/*, /api/rbac/users/{userId}/roles.
 */

import { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, X, Clock } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { Select } from '@/components/common/Select';
import { Input } from '@/components/common/Input';
import { Badge } from '@/components/common/Badge';
import { Card, CardBody } from '@/components/common/Card';
import { rbacService } from '@/api/services';
import type { RbacRole, UserRoleAssignment } from '@/api/types';
import { usePermissionStore } from '@/store/slices/permissionSlice';
import { useToast } from '@/hooks/useToast';
import { formatDate } from '@/utils/date.utils';

interface UserRolesPanelProps {
  userId: string;
}

export function UserRolesPanel({ userId }: UserRolesPanelProps) {
  const toast = useToast();
  const canView = usePermissionStore((s) => s.isOwner || !s.loaded || s.permissions.has('userMgmt.role.view'));
  const canAssign = usePermissionStore((s) => s.isOwner || !s.loaded || s.permissions.has('userMgmt.role.assign'));

  const [assignments, setAssignments] = useState<UserRoleAssignment[]>([]);
  const [roles, setRoles] = useState<RbacRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!userId || !canView) return;
    try {
      setIsLoading(true);
      const [userRolesRes, rolesRes] = await Promise.all([
        rbacService.getUserRoles(userId),
        rbacService.getRoles(),
      ]);
      if (userRolesRes.statusCode === 200) setAssignments(userRolesRes.result ?? []);
      if (rolesRes.statusCode === 200) setRoles(rolesRes.result ?? []);
    } catch (error) {
      console.error('Error loading user roles:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, canView]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (!canView) return null;

  const heldRoleIds = new Set(assignments.map((a) => a.roleId));
  const assignableRoles = roles.filter((r) => !heldRoleIds.has(r.id));

  const handleAssign = async () => {
    if (!selectedRoleId) return;
    try {
      setIsSubmitting(true);
      const response = await rbacService.assignRole({
        userId,
        roleId: selectedRoleId,
        validUntil: validUntil ? new Date(`${validUntil}T23:59:59`).getTime() : undefined,
      });
      if (response.statusCode === 200) {
        toast.success('Role assigned successfully.');
        setShowAssign(false);
        setSelectedRoleId('');
        setValidUntil('');
        void fetchData();
      } else {
        toast.error(response.statusMessage || 'Failed to assign role');
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      if (!(error as { isPermissionDenied?: boolean })?.isPermissionDenied) {
        toast.error('Failed to assign role');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevoke = async (assignment: UserRoleAssignment) => {
    if (!window.confirm(`Remove the "${assignment.roleName || assignment.roleCode}" role from this user?`)) {
      return;
    }
    try {
      const response = await rbacService.revokeRole({ userId, roleId: assignment.roleId });
      if (response.statusCode === 200) {
        toast.success('Role removed successfully.');
        void fetchData();
      } else {
        toast.error(response.statusMessage || 'Failed to remove role');
      }
    } catch (error) {
      console.error('Error revoking role:', error);
      if (!(error as { isPermissionDenied?: boolean })?.isPermissionDenied) {
        toast.error('Failed to remove role');
      }
    }
  };

  return (
    <Card className="mt-6">
      <CardBody>
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-lg bg-primary-50 p-2 text-primary dark:bg-primary-900/30">
            <Shield className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground dark:text-foreground-dark">Roles</h3>
            <p className="text-xs text-foreground-muted dark:text-foreground-dark-muted">
              What this user can do — the combined access of all roles below.
            </p>
          </div>
          {canAssign && !showAssign && (
            <Button size="sm" onClick={() => setShowAssign(true)}>
              <Plus className="h-3.5 w-3.5" />
              <span className="ml-1">Assign role</span>
            </Button>
          )}
        </div>

        {/* assign form */}
        {showAssign && (
          <div className="mb-4 flex flex-col gap-3 rounded-lg border border-border bg-secondary-50 p-3.5 dark:border-border-dark dark:bg-secondary-900/20 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <Select
                label="Role"
                options={assignableRoles.map((r) => ({
                  value: r.id,
                  label: `${r.name}${r.isSystem ? ' (system)' : ''}`,
                }))}
                value={selectedRoleId}
                onChange={setSelectedRoleId}
                placeholder="Select a role…"
              />
            </div>
            <Input
              type="date"
              label="Valid until (optional)"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
            />
            <div className="flex gap-2">
              <Button onClick={handleAssign} disabled={!selectedRoleId} loading={isSubmitting}>
                Assign
              </Button>
              <Button variant="outline" onClick={() => setShowAssign(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* assignments */}
        {isLoading ? (
          <div className="py-6 text-center text-sm text-foreground-muted">Loading roles…</div>
        ) : assignments.length === 0 ? (
          <div className="py-6 text-center text-sm text-foreground-muted">
            No roles assigned — this user has no access until a role is given.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {assignments.map((a) => (
              <span
                key={a.roleId}
                className="inline-flex items-center gap-2 rounded-full border border-primary-100 bg-primary-50 py-1.5 pl-3 pr-1.5 text-sm text-primary-700 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-400"
              >
                <span className="font-medium">{a.roleName || a.roleCode}</span>
                {a.validUntil && (
                  <Badge variant="warning" size="sm">
                    <Clock className="mr-1 h-3 w-3" />
                    until {formatDate(new Date(a.validUntil).toISOString())}
                  </Badge>
                )}
                {canAssign && (
                  <button
                    onClick={() => handleRevoke(a)}
                    title="Remove role"
                    className="rounded-full p-1 text-primary-400 hover:bg-primary-100 hover:text-primary-700 dark:hover:bg-primary-800/60"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default UserRolesPanel;
